import type { CompanyShellRow } from "../../generated/core-api";
import type {
  CompanyShellCsvColumn,
  CompanyShellCsvParseError,
} from "../types";
import {
  collectCompanyShellLiveRowIssues,
  isDuplicateCompanyCodeParseError,
  isRequiredCompanyShellParseError,
} from "./collectCompanyShellLiveRowIssues";
import {
  isBlockingCompanyShellParseError,
  isCompanyShellImportNote,
} from "./parseCompanyShellCsv";

/**
 * Build the live, structured Import Notes list. CSV-shape notes remain tied
 * to the uploaded file, while editable required/duplicate issues are derived
 * from current Review values and disappear as they are corrected.
 */
export function deriveCompanyShellImportNotes(
  rows: CompanyShellRow[],
  columns: CompanyShellCsvColumn[],
  parseErrors: CompanyShellCsvParseError[],
  rowCsvLineNumbers: number[]
): CompanyShellCsvParseError[] {
  const rowIndexByCsvLine = new Map<number, number>();
  rowCsvLineNumbers.forEach((csvLineNumber, rowIndex) => {
    rowIndexByCsvLine.set(csvLineNumber, rowIndex);
  });

  const notes: CompanyShellCsvParseError[] = [
    ...parseErrors.filter(isCompanyShellImportNote),
    ...collectCompanyShellLiveRowIssues(
      rows,
      columns,
      rowCsvLineNumbers
    ).map((issue) => ({
      csv_line_number: issue.csvLineNumber,
      column: issue.column,
      message: issue.message,
    })),
  ];

  for (const error of parseErrors) {
    if (
      isBlockingCompanyShellParseError(error) ||
      isCompanyShellImportNote(error) ||
      isRequiredCompanyShellParseError(error) ||
      isDuplicateCompanyCodeParseError(error) ||
      error.csv_line_number === undefined ||
      !rowIndexByCsvLine.has(error.csv_line_number)
    ) {
      continue;
    }
    notes.push(error);
  }

  const unique = new Map<string, CompanyShellCsvParseError>();
  for (const note of notes) {
    const key = `${note.csv_line_number ?? ""}\u0000${
      note.column ?? ""
    }\u0000${note.message}`;
    unique.set(key, note);
  }

  return [...unique.values()].sort((left, right) => {
    const rowDifference =
      (left.csv_line_number ?? Number.POSITIVE_INFINITY) -
      (right.csv_line_number ?? Number.POSITIVE_INFINITY);
    if (rowDifference !== 0) return rowDifference;
    const columnDifference = (left.column ?? "").localeCompare(
      right.column ?? "",
      "en-NZ"
    );
    return columnDifference !== 0
      ? columnDifference
      : left.message.localeCompare(right.message, "en-NZ");
  });
}
